import { SubscriptionOfferType } from "@bluebottlecoffee/design-system/components";
import {
  Subscription,
  SubscriptionLineItem,
} from "@chordcommerce/react-autonomy";
import { trackEvent } from "./track-event";

type EventSubscriptionLineItem = Omit<
  SubscriptionLineItem,
  | "images"
  | "endDate"
  | "intervalLength"
  | "intervalUnits"
  | "interval"
  | "displayPrice"
>;

type EventSubscriptionALCAddOnLineItem = Omit<
  EventSubscriptionLineItem,
  "subscriptionType"
>;

type SubscriptionEventProps = Omit<
  Partial<Subscription>,
  "id" | "lineItems"
> & {
  id: Subscription["id"];
  lineItems?: EventSubscriptionLineItem[];
};

export function toEventSubscriptionLineItem(
  lineItem: SubscriptionLineItem,
): EventSubscriptionLineItem {
  return {
    id: lineItem.id,
    name: lineItem.name,
    // @ts-ignore
    subscriptionType: lineItem.subscriptionType,
    optionsText: lineItem.optionsText,
    description: lineItem.description,
    productId: lineItem.productId,
    productSlug: lineItem.productSlug,
    variantId: lineItem.variantId,
    sku: lineItem.sku,
    quantity: lineItem.quantity,
    price: lineItem.price,
  };
}

function toEventSubscriptionALCAddOnLineItem(
  lineItem: SubscriptionLineItem,
): EventSubscriptionALCAddOnLineItem {
  return {
    description: lineItem.description,
    id: lineItem.id,
    name: lineItem.name,
    // @ts-ignore TODO: remove this once the SDK is updated
    oneTime: lineItem.oneTime,
    optionsText: lineItem.optionsText,
    price: lineItem.price,
    productId: lineItem.productId,
    productSlug: lineItem.productSlug,
    quantity: lineItem.quantity,
    sku: lineItem.sku,
    variantId: lineItem.variantId,
  };
}

export function toSubscriptionEventProps(
  subscription: Subscription,
  lineItemTransformer: (
    lineItem: SubscriptionLineItem,
  ) => EventSubscriptionLineItem | EventSubscriptionALCAddOnLineItem,
): SubscriptionEventProps {
  return {
    id: subscription.id,
    state: subscription.state,
    actionableDate: subscription.actionableDate,
    intervalLength: subscription.intervalLength,
    intervalUnits: subscription.intervalUnits,
    lineItems: subscription.lineItems?.map(lineItemTransformer),
  };
}

export function trackSubscriptionCancellationStarted(
  subscription: Subscription,
) {
  const eventProps = toSubscriptionEventProps(
    subscription,
    toEventSubscriptionLineItem,
  );
  return () => trackEvent("Subscription Cancellation Started", eventProps);
}

export function trackSubscriptionOfferAccepted(subscription: Subscription) {
  const eventProps = toSubscriptionEventProps(
    subscription,
    toEventSubscriptionLineItem,
  );

  return (offerType: SubscriptionOfferType) => {
    const eventPropsWithOffer = { ...eventProps, offerType };
    trackEvent("Subscription Offer Accepted", eventPropsWithOffer);
  };
}

export function trackSubscriptionCancellationDeflected(
  subscription: Subscription,
) {
  const eventProps = toSubscriptionEventProps(
    subscription,
    toEventSubscriptionLineItem,
  );
  return () => trackEvent("Subscription Cancellation Deflected", eventProps);
}

export function trackSubscriptionALCAddOnVariantAdded(
  addedALCLineItem: SubscriptionLineItem,
  subscription: Subscription,
) {
  const eventProps = {
    ...toSubscriptionEventProps(
      subscription,
      toEventSubscriptionALCAddOnLineItem,
    ),
    addedALCLineItem: toEventSubscriptionALCAddOnLineItem(addedALCLineItem),
  };
  return () => trackEvent("Subscription ALC Item Added", eventProps);
}

export function trackSubscriptionALCAddOnVariantRemoved(
  removedALCLineItem: SubscriptionLineItem,
  subscription: Subscription,
) {
  const eventProps = {
    ...toSubscriptionEventProps(
      subscription,
      toEventSubscriptionALCAddOnLineItem,
    ),
    removedALCLineItem: toEventSubscriptionALCAddOnLineItem(removedALCLineItem),
  };
  return () => trackEvent("Subscription ALC Item Removed", eventProps);
}
